import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Radio,
  Row,
  Select
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import {
  useClassifierServiceGetInvestigationClassifierEndpoint,
  useClassifierServiceGetInvestigationTestTypeEndpoint,
  useCounterpartServiceGetInternalInstitutionEndpoint,
  useInvestigationsServiceGetInvestigationTestsEndpointKey,
  useInvestigationsServiceGetListSpecimensEndpointKey,
  useInvestigationsServiceGetSpecimenEndpoint,
  useInvestigationsServiceGetSpecimenEndpointKey,
  useInvestigationsServiceUpdateExternalInvestigationResultEndpoint
} from 'src/api/queries'
import {
  ApiError,
  InvestigationTestResultDto,
  SpecimenLaboratoryStatus,
  UpdateExternalInvestigationResultRequest
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  CustomItemRender,
  InternalInstitutionsSelect,
  message,
  pickReferralDoctor,
  ReferralDoctorFields,
  SaveConfirm,
  Text,
  Upload
} from 'src/components'
import { useStoredResultOptions } from 'src/features/tests/hooks/useStoredResultOptions'
import { useFileHandler } from 'src/hooks'
import {
  dayMonthYearHourMinutesFormatWithDot,
  disabledFutureDate
} from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { ExternalResultReceptionFields } from './ExternalResultReceptionFields'
import { useExternalResultReception } from './useExternalResultReception'

type SourceType = 'registered' | 'freeText'

type Props = {
  onCancel: () => void
  onSuccess?: () => void
  result: InvestigationTestResultDto
  /** Specimen the result hangs off, needed to reach the reception fields. */
  specimenId?: string
  /** The result DTO itself doesn't carry the referring doctor; it comes from
   *  the test/demand behind the result, so the caller passes it through. */
  referralDoctorUserId?: string | null
  referralDoctorName?: string | null
  /** Display name of the registered referring doctor (when referralDoctorUserId
   *  is set), so the select can show it instead of the raw id before its own
   *  list load reaches that doctor. */
  referralDoctorUserName?: string | null
  /** The investigation type classifier id. When available, result/value
   *  options are read from it (same source AddResultModal uses to create the
   *  result), since the standalone test-type endpoint doesn't reliably
   *  hydrate nested value options. */
  typeId?: string
} & ModalProps

export const EditExternalResultModal = ({
  onCancel,
  onSuccess,
  result,
  specimenId,
  referralDoctorUserId,
  referralDoctorName,
  referralDoctorUserName,
  typeId,
  ...props
}: Props) => {
  const { t, currentLang } = useLocales()
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const lang = currentLang.value

  const [sourceType, setSourceType] = useState<SourceType>(
    result.sourceLaboratoryName && !result.sourceLaboratoryId
      ? 'freeText'
      : 'registered'
  )
  const referralDoctorSource: 'registered' | 'freeText' =
    referralDoctorName && !referralDoctorUserId ? 'freeText' : 'registered'
  const [isSaving, setIsSaving] = useState(false)

  const { onFileChange, saveFiles, setExistingFiles, isUploading } =
    useFileHandler({ form, folder: 'results' })

  const { applyReception } = useExternalResultReception()

  const { data: specimen } = useInvestigationsServiceGetSpecimenEndpoint(
    { id: String(specimenId) },
    [specimenId],
    { enabled: !!specimenId }
  )

  const initialIsConform = specimen?.laboratoryStatus
    ? specimen.laboratoryStatus !== SpecimenLaboratoryStatus.INCONFORM
    : undefined

  // The result DTO only carries the registered laboratory's id, not its name,
  // so it's fetched here to seed the select's label instead of showing the id.
  const { data: sourceInstitution } =
    useCounterpartServiceGetInternalInstitutionEndpoint(
      { id: String(result.sourceLaboratoryId) },
      [result.sourceLaboratoryId],
      { enabled: !!result.sourceLaboratoryId }
    )

  const resultOptionId = Form.useWatch('resultOptionId', form)

  // The standalone test-type-by-id endpoint doesn't reliably hydrate nested
  // value options, so it's only used (when the caller didn't already pass
  // typeId) to read the test type's own classifierId — the id of its parent
  // investigation type — which is then read the same reliable way
  // AddResultModal/EditTestResultModal do.
  const { data: testTypeMeta, isLoading: isTestTypeMetaLoading } =
    useClassifierServiceGetInvestigationTestTypeEndpoint(
      { id: String(result.testType?.id) },
      [result.testType?.id],
      { enabled: !typeId && !!result.testType?.id }
    )

  const effectiveTypeId = typeId ?? testTypeMeta?.classifierId

  const { data: investigation, isLoading: isInvestigationLoading } =
    useClassifierServiceGetInvestigationClassifierEndpoint(
      { id: String(effectiveTypeId) },
      [effectiveTypeId],
      { enabled: !!effectiveTypeId }
    )

  const isLoading = isTestTypeMetaLoading || isInvestigationLoading

  const resultOptions =
    investigation?.testTypes?.find((item) => item.id === result.testType?.id)
      ?.resultOptions ?? []
  const valueOptions =
    resultOptions.find((option) => option.id === resultOptionId)
      ?.valueOptions ?? []

  const { resultOptionItems, valueOptionItems } = useStoredResultOptions({
    result,
    lang,
    resultOptions,
    valueOptions,
    resultOptionId,
    isOptionsLoading: isLoading
  })

  useEffect(() => {
    const existingFileIds = (result.mediaItems ?? [])
      .map((media) => media.id)
      .filter((id): id is string => !!id)

    setExistingFiles(existingFileIds)

    form.setFieldsValue({
      sampleCollectedAt: result.sampleCollectedAt
        ? dayjs(result.sampleCollectedAt)
        : undefined,
      resultDate: result.resultDate ? dayjs(result.resultDate) : undefined,
      sourceLaboratoryId: result.sourceLaboratoryId ?? undefined,
      sourceLaboratoryName: result.sourceLaboratoryName ?? undefined,
      referralDoctorUserId: referralDoctorUserId ?? undefined,
      referralDoctorName: referralDoctorName ?? undefined,
      resultOptionId: result.resultOption?.id ?? undefined,
      resultValueOptionId: result.resultValueOption?.id ?? undefined,
      resultValue: result.resultValue ?? undefined,
      docs: (result.mediaItems ?? []).map((media) => ({
        uid: String(media.id),
        name: media.name ?? `File-${media.id}`,
        status: 'done'
      }))
    })
  }, [result, referralDoctorUserId, referralDoctorName])

  // Conformity and reception date live on the specimen, which loads separately.
  useEffect(() => {
    if (!specimen) return

    form.setFieldsValue({
      receivedAt: specimen.receivedAt ? dayjs(specimen.receivedAt) : undefined,
      isConform: initialIsConform
    })
  }, [specimen])

  const { mutateAsync: updateExternalResult } =
    useInvestigationsServiceUpdateExternalInvestigationResultEndpoint()

  const invalidate = () =>
    [
      useInvestigationsServiceGetListSpecimensEndpointKey,
      useInvestigationsServiceGetInvestigationTestsEndpointKey,
      useInvestigationsServiceGetSpecimenEndpointKey
    ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))

  const onSourceTypeChange = (value: SourceType) => {
    setSourceType(value)
    form.resetFields(['sourceLaboratoryId', 'sourceLaboratoryName'])
  }

  // A qualitative value belongs to the option it was picked under, so changing
  // the option drops the stale value.
  const onResultOptionChange = () => form.resetFields(['resultValueOptionId'])

  // Re-sending an unchanged reception would append a duplicate comment, so the
  // specimen is only touched when one of its fields actually moved.
  const hasReceptionChanged = (values: {
    receivedAt?: dayjs.Dayjs
    isConform?: boolean
    nonConformityComment?: string
  }) => {
    const receivedAtChanged = specimen?.receivedAt
      ? !dayjs(specimen.receivedAt).isSame(values.receivedAt)
      : !!values.receivedAt

    return (
      receivedAtChanged ||
      values.isConform !== initialIsConform ||
      !!values.nonConformityComment
    )
  }

  const onSubmit = async () => {
    const values = await form.validateFields()

    if (!result.id) {
      return message.error(t('errors.something_went_wrong'))
    }

    setIsSaving(true)

    try {
      const mediaItemIds = await saveFiles()

      const requestBody: UpdateExternalInvestigationResultRequest = {
        resultOptionId: values.resultOptionId || undefined,
        resultValueOptionId: values.resultValueOptionId || undefined,
        resultValue: values.resultValue ?? undefined,
        sampleCollectedAt: dayjs(values.sampleCollectedAt).format(),
        resultDate: values.resultDate
          ? dayjs(values.resultDate).format()
          : null,
        sourceLaboratoryId:
          sourceType === 'registered'
            ? values.sourceLaboratoryId || undefined
            : undefined,
        sourceLaboratoryName:
          sourceType === 'freeText'
            ? values.sourceLaboratoryName || undefined
            : undefined,
        ...pickReferralDoctor(values),
        mediaItemIds: mediaItemIds.length ? mediaItemIds : undefined
      }

      await updateExternalResult({
        id: result.id,
        requestBody
      })

      message.success(t('persons.external_result.updated'))

      // The result is already saved at this point, so a failing reception only
      // warns instead of rolling the whole edit back.
      if (specimenId && hasReceptionChanged(values)) {
        try {
          await applyReception(specimenId, values)
        } catch {
          message.warning(t('persons.external_result.reception_not_saved'))
        }
      }

      invalidate()
      onSuccess?.()
      onCancel()
    } catch (err) {
      formErrorHandler((err as ApiError).body?.errors)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      open
      centered
      width={800}
      footer={null}
      maskClosable={false}
      title={t('persons.external_result.edit_title')}
      onCancel={onCancel}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        {/* Source laboratory comes first: it is what the reception starts from,
            before the sample details. */}
        <Row gutter={[16, 12]}>
          <Col span={24}>
            <Form.Item label={t('persons.external_result.source_laboratory')}>
              <Radio.Group
                value={sourceType}
                onChange={(e) => onSourceTypeChange(e.target.value)}
              >
                <Radio value="registered">
                  {t('persons.external_result.source_registered')}
                </Radio>
                <Radio value="freeText">
                  {t('persons.external_result.source_free_text')}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={24}>
            {sourceType === 'registered' ? (
              <Form.Item name="sourceLaboratoryId">
                <InternalInstitutionsSelect
                  placeholder={t('persons.external_result.source_laboratory')}
                  initialOptions={
                    result.sourceLaboratoryId && sourceInstitution?.name
                      ? [
                          {
                            value: result.sourceLaboratoryId,
                            label: sourceInstitution.name
                          }
                        ]
                      : undefined
                  }
                />
              </Form.Item>
            ) : (
              <Form.Item name="sourceLaboratoryName">
                <Input
                  placeholder={t(
                    'persons.external_result.source_laboratory_name'
                  )}
                />
              </Form.Item>
            )}
          </Col>

          <ReferralDoctorFields
            form={form}
            initialSource={referralDoctorSource}
            initialUserOption={
              referralDoctorUserId && referralDoctorUserName
                ? { value: referralDoctorUserId, label: referralDoctorUserName }
                : undefined
            }
          />
        </Row>

        <Divider />

        <Row gutter={[16, 12]}>
          <Col span={24}>
            <Form.Item label={t('specimens.test_type')}>
              <Input
                readOnly
                value={result.testType?.translation?.[lang] ?? '-'}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="resultOptionId" label={t('tests_results.result')}>
              <Select
                allowClear
                loading={isLoading}
                placeholder={t('tests_results.result')}
                options={resultOptionItems}
                onChange={onResultOptionChange}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="resultValueOptionId"
              label={t('specimens.result_option')}
            >
              <Select
                allowClear
                disabled={!resultOptionId}
                placeholder={t('specimens.result_option')}
                options={valueOptionItems}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="resultValue"
              label={t('investigations.result_numeric_value')}
            >
              <InputNumber
                className="full-width"
                placeholder={t('investigations.result_numeric_value')}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="sampleCollectedAt"
              label={t('persons.external_result.sample_collected_at')}
              rules={requiredRules}
            >
              <DatePicker
                className="full-width"
                showTime
                showSecond={false}
                disabledDate={disabledFutureDate}
                format={dayMonthYearHourMinutesFormatWithDot}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="resultDate"
              label={t('persons.external_result.result_date')}
            >
              <DatePicker
                className="full-width"
                showTime
                showSecond={false}
                disabledDate={disabledFutureDate}
                format={dayMonthYearHourMinutesFormatWithDot}
              />
            </Form.Item>
          </Col>

          {!!specimenId && (
            <ExternalResultReceptionFields
              form={form}
              initialIsConform={initialIsConform}
            />
          )}

          <Col span={24}>
            <Text type="secondary" size="sm">
              {t('stock_applications.uploaded_documents')}
            </Text>
            <Form.Item name="docs" className="full-width">
              <Upload
                onChange={onFileChange}
                customRequest={() => null}
                itemRender={(_, file, __, { remove }) => (
                  <CustomItemRender file={file} onRemove={remove} />
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        <Flex justify="center" gap={8}>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>

          <SaveConfirm
            title={`${t('common.confirm')}?`}
            onConfirm={onSubmit}
            okButtonProps={{ loading: isSaving || isUploading }}
          >
            <Button type="primary">{t('common.confirm')}</Button>
          </SaveConfirm>
        </Flex>
      </Form>
    </Modal>
  )
}
